import React, { useEffect, useState } from "react";
import { api, fmtBRL, fmtDate, STATUS_LABELS, STATUS_CLASS } from "@/lib/api";
import { Coins, TrendUp, Warning } from "@phosphor-icons/react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";

export default function Financial() {
  const [stats, setStats] = useState(null);
  const [invoices, setInvoices] = useState([]);
  useEffect(() => {
    api.get("/stats/admin").then((r) => setStats(r.data));
    api.get("/invoices").then((r) => setInvoices(r.data));
  }, []);
  if (!stats) return <div className="p-10 text-slate-500">Carregando…</div>;

  const paid = invoices.filter((i) => i.status === "pago");
  const pending = invoices.filter((i) => i.status !== "pago");

  return (
    <div className="p-8 max-w-[1400px]">
      <div className="mb-8">
        <div className="eyebrow mb-1">Financeiro</div>
        <h1 className="font-display text-4xl tracking-tight">Cobrança & Repasses</h1>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Metric label="Recebido" value={fmtBRL(paid.reduce((a,x)=>a+x.amount,0))} icon={Coins} tone="success" testId="fin-recebido" />
        <Metric label="Pendente" value={fmtBRL(pending.reduce((a,x)=>a+x.amount,0))} icon={Warning} tone="warn" testId="fin-pendente" />
        <Metric label="Comissões" value={fmtBRL(stats.commissions)} icon={TrendUp} testId="fin-comissoes" />
        <Metric label="Taxa Inadimplência" value={`${stats.default_rate}%`} icon={Warning} tone={stats.default_rate>10?"danger":"neutral"} testId="fin-taxa" />
      </div>

      <div className="card-flat p-6 mb-6">
        <div className="eyebrow">Evolução mensal</div>
        <div className="font-display text-xl mt-1 mb-4">Receita nos últimos 6 meses</div>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={stats.monthly}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E0" />
            <XAxis dataKey="month" stroke="#94a3b8" />
            <YAxis stroke="#94a3b8" tickFormatter={(v)=>`R$${(v/1000).toFixed(0)}k`} />
            <Tooltip formatter={(v)=>fmtBRL(v)} contentStyle={{background:"#fff",border:"1px solid #E7E5E0",borderRadius:6}} />
            <Line type="monotone" dataKey="receita" stroke="#1E2A44" strokeWidth={2.5} dot={{r:4}} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="card-flat p-6" data-testid="invoices-table">
        <div className="eyebrow mb-3">Faturas recentes</div>
        <table className="w-full text-sm">
          <thead className="text-slate-500 eyebrow">
            <tr>
              <th className="text-left py-2">ID</th>
              <th className="text-left py-2">Contrato</th>
              <th className="text-left py-2">Vencimento</th>
              <th className="text-left py-2">Valor</th>
              <th className="text-left py-2">Método</th>
              <th className="text-left py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {invoices.slice(0, 20).map((i) => (
              <tr key={i.invoice_id} className="border-t border-[var(--line)]">
                <td className="py-2 mono text-slate-500">{i.invoice_id.slice(0,10)}</td>
                <td className="py-2 mono text-slate-500">{i.contract_id.slice(0,10)}</td>
                <td className="py-2">{fmtDate(i.due_date)}</td>
                <td className="py-2 mono">{fmtBRL(i.amount)}</td>
                <td className="py-2">{i.method ? i.method.toUpperCase() : "—"}</td>
                <td className="py-2"><span className={`status-pill ${STATUS_CLASS[i.status]}`}>{STATUS_LABELS[i.status]}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
function Metric({ label, value, icon: Icon, tone, testId }) {
  const color = tone === "danger" ? "#DC2626" : tone === "warn" ? "#D97706" : tone === "success" ? "#059669" : "#1E2A44";
  return (
    <div className="card-flat p-5" data-testid={testId}>
      <div className="flex items-start justify-between"><div className="eyebrow">{label}</div><Icon size={18} weight="duotone" color={color} /></div>
      <div className="metric-value text-3xl mt-3" style={{color}}>{value}</div>
    </div>
  );
}
